import { useFilterStore } from '../stores/filterStore.js';
import { FilterRenderer } from './filters/FilterRenderer.js';
import { FilterEventManager } from './filters/FilterEventManager.js';
import { FilterDataLoader } from './filters/FilterDataLoader.js';
import { FilterUIManager } from './filters/FilterUIManager.js';
import { artisanalMinesFilter } from './ArtisanalMinesFilter.js';

export class FilterPanel {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      autoLoad: true,
      ...options
    };
    this.filters = {};
    this.unsubscribe = null;

    // Initialize component dependencies
    this.renderer = new FilterRenderer(container);
    this.dataLoader = new FilterDataLoader();
    this.uiManager = new FilterUIManager(container);
    this.eventManager = new FilterEventManager(container, {
      onMultiSelectToggle: this.handleMultiSelectToggle.bind(this),
      onFilterChange: this.handleFilterChange.bind(this),
      onMineralCategoryChange: this.handleMineralCategoryChange.bind(this),
      onReset: this.resetFilters.bind(this),
      onDocumentClick: this.handleDocumentClick.bind(this)
    });

    this.init();
  }

  async init() {
    this.renderer.render();
    this.eventManager.attachEventListeners();

    // Re-attach artisanal toggle after FilterRenderer re-creates the DOM
    artisanalMinesFilter.reattachEventListener();

    // Only subscribe to store if in browser environment
    if (typeof window !== 'undefined') {
      this.subscribeToStore();

      if (this.options.autoLoad) {
        await this.loadFilterOptions();
      }
    }
  }

  subscribeToStore() {
    this.unsubscribe = useFilterStore.subscribe(
      (state) => state.filters,
      (filters) => {
        this.updateUIFromFilters(filters);
      }
    );

    // Initialize with current filters
    this.updateUIFromFilters(useFilterStore.getState().filters);
  }

  async loadFilterOptions() {
    try {
      const filterOptions = await this.dataLoader.loadFilterOptions();
      this.renderer.populateFilterOptions(filterOptions);
      this.setDefaultSelections();
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  }

  setDefaultSelections() {
    // Set default mine status selections (Development and Active)
    const defaultStatuses = ['Development', 'Active'];

    // Initialize internal filters state
    if (!this.filters.mine_status) {
      this.filters.mine_status = [];
    }

    defaultStatuses.forEach(status => {
      const checkbox = this.container.querySelector(`input[value="${status}"][data-filter="mine_status"]`);
      if (checkbox) {
        checkbox.checked = true;
        // Update internal filters state
        if (!this.filters.mine_status.includes(status)) {
          this.filters.mine_status.push(status);
        }
      }
    });

    // Update toggle text for mine status
    this.renderer.updateMultiSelectText('mine_status', this.filters.mine_status.length);

    // Update store with default selections - use correct property name
    const filterStore = useFilterStore.getState();
    filterStore.setFilter('mineStatus', this.filters.mine_status);

    console.log('✅ Default filters set:', { mineStatus: this.filters.mine_status });
  }

  // Event handler methods for delegation to extracted components
  handleMultiSelectToggle(toggle) {
    this.uiManager.toggleMultiSelect(toggle);
  }

  handleDocumentClick() {
    this.uiManager.closeAllMultiSelects();
  }

  handleFilterChange(input) {
    const filterType = input.dataset.filter;
    const value = input.value;
    const checked = input.checked;

    console.log('🔧 handleFilterChange:', { filterType, value, checked });

    // Update local filters state
    if (!this.filters[filterType]) {
      this.filters[filterType] = [];
    }

    if (checked) {
      if (!this.filters[filterType].includes(value)) {
        this.filters[filterType].push(value);
      }
    } else {
      this.filters[filterType] = this.filters[filterType].filter(v => v !== value);
    }

    console.log('📋 Updated filters:', this.filters);

    // Update multi-select toggle text
    this.renderer.updateMultiSelectText(filterType, this.filters[filterType].length);

    // Update store immediately with each change - map to correct store property names
    const filterStore = useFilterStore.getState();
    const storePropertyMap = {
      'country': 'countries',
      'minerals': 'specificCommodities', // Map minerals to existing store property
      'mine_type': 'mineTypes',
      'mine_status': 'mineStatus',
      'mineralType': 'mineralType'
    };
    const storeProperty = storePropertyMap[filterType] || filterType;
    filterStore.setFilter(storeProperty, this.filters[filterType]);
    console.log('✅ Store updated with filter:', storeProperty, this.filters[filterType]);
  }

  handleMineralCategoryChange(input) {
    const value = input.value;
    const checked = input.checked;

    console.log('🔧 handleMineralCategoryChange:', { value, checked });

    if (!checked) {
      // If unchecking a category, remove it from filters and uncheck associated minerals
      this.filters.minerals = this.filters.minerals.filter(v => v !== value);

      if (value === 'critical-minerals-only') {
        // Uncheck all critical minerals
        const criticalMinerals = this.dataLoader.getCriticalMinerals();
        criticalMinerals.forEach(mineral => {
          const checkbox = this.container.querySelector(`input[value="${mineral}"][data-filter="minerals"]`);
          if (checkbox && checkbox.checked) {
            checkbox.checked = false;
            this.filters.minerals = this.filters.minerals.filter(v => v !== mineral);
          }
        });
      } else if (value === 'non-critical-minerals-only') {
        // Uncheck all non-critical minerals
        const nonCriticalMinerals = this.dataLoader.getNonCriticalMinerals();
        nonCriticalMinerals.forEach(mineral => {
          const checkbox = this.container.querySelector(`input[value="${mineral}"][data-filter="minerals"]`);
          if (checkbox && checkbox.checked) {
            checkbox.checked = false;
            this.filters.minerals = this.filters.minerals.filter(v => v !== mineral);
          }
        });
      }

      // Update UI and store
      this.renderer.updateMultiSelectText('minerals', this.filters.minerals.length);
      const filterStore = useFilterStore.getState();
      filterStore.setFilter('specificCommodities', this.filters.minerals);
      console.log('✅ Category unchecked, updated mineral filters:', this.filters.minerals);
      return;
    }

    // Initialize minerals filter if not exists
    if (!this.filters.minerals) {
      this.filters.minerals = [];
    }

    if (value === 'critical-minerals-only') {
      // Uncheck non-critical category
      const nonCriticalCheckbox = this.container.querySelector('input[value="non-critical-minerals-only"]');
      if (nonCriticalCheckbox && nonCriticalCheckbox.checked) {
        nonCriticalCheckbox.checked = false;
        this.filters.minerals = this.filters.minerals.filter(v => v !== 'non-critical-minerals-only');
      }

      // Uncheck all non-critical minerals
      const nonCriticalMinerals = this.dataLoader.getNonCriticalMinerals();
      nonCriticalMinerals.forEach(mineral => {
        const checkbox = this.container.querySelector(`input[value="${mineral}"][data-filter="minerals"]`);
        if (checkbox && checkbox.checked) {
          checkbox.checked = false;
          this.filters.minerals = this.filters.minerals.filter(v => v !== mineral);
        }
      });

      // Add critical category to filters
      if (!this.filters.minerals.includes('critical-minerals-only')) {
        this.filters.minerals.push('critical-minerals-only');
      }

      // Auto-select all critical minerals
      const criticalMinerals = this.dataLoader.getCriticalMinerals();
      criticalMinerals.forEach(mineral => {
        const checkbox = this.container.querySelector(`input[value="${mineral}"][data-filter="minerals"]`);
        if (checkbox) {
          checkbox.checked = true;
          if (!this.filters.minerals.includes(mineral)) {
            this.filters.minerals.push(mineral);
          }
        }
      });

    } else if (value === 'non-critical-minerals-only') {
      // Uncheck critical category
      const criticalCheckbox = this.container.querySelector('input[value="critical-minerals-only"]');
      if (criticalCheckbox && criticalCheckbox.checked) {
        criticalCheckbox.checked = false;
        this.filters.minerals = this.filters.minerals.filter(v => v !== 'critical-minerals-only');
      }

      // Uncheck all critical minerals
      const criticalMinerals = this.dataLoader.getCriticalMinerals();
      criticalMinerals.forEach(mineral => {
        const checkbox = this.container.querySelector(`input[value="${mineral}"][data-filter="minerals"]`);
        if (checkbox && checkbox.checked) {
          checkbox.checked = false;
          this.filters.minerals = this.filters.minerals.filter(v => v !== mineral);
        }
      });

      // Add non-critical category to filters
      if (!this.filters.minerals.includes('non-critical-minerals-only')) {
        this.filters.minerals.push('non-critical-minerals-only');
      }

      // Auto-select all non-critical minerals
      const nonCriticalMinerals = this.dataLoader.getNonCriticalMinerals();
      nonCriticalMinerals.forEach(mineral => {
        const checkbox = this.container.querySelector(`input[value="${mineral}"][data-filter="minerals"]`);
        if (checkbox) {
          checkbox.checked = true;
          if (!this.filters.minerals.includes(mineral)) {
            this.filters.minerals.push(mineral);
          }
        }
      });
    }

    console.log('📋 Updated mineral filters:', this.filters.minerals);

    // Update multi-select toggle text
    this.renderer.updateMultiSelectText('minerals', this.filters.minerals.length);

    // Update store with mineral selections
    const filterStore = useFilterStore.getState();
    filterStore.setFilter('specificCommodities', this.filters.minerals);
    console.log('✅ Store updated with mineral filters:', this.filters.minerals);
  }

  updateUIFromFilters(filters) {
    // Handle null/undefined filters
    if (!filters) {
      filters = {};
    }

    this.filters = { ...filters };
    this.renderer.updateUIFromFilters(filters);
  }

  resetFilters() {
    console.log('🔄 Resetting filters to defaults...');

    // Reset internal state
    this.filters = {};

    // Reset UI via renderer
    this.renderer.resetUI();

    // Reset artisanal toggle to OFF
    artisanalMinesFilter.setFilterState(false);

    // Reset FilterStore to defaults
    const filterStore = useFilterStore.getState();
    filterStore.resetFilters();

    // Re-apply default selections (Development + Active)
    setTimeout(() => {
      this.setDefaultSelections();
      // Explicitly update minerals count display to 0 since no minerals are selected by default
      this.renderer.updateMultiSelectText('minerals', 0);
    }, 50); // Small delay to ensure DOM is updated

    console.log('✅ Filters reset and defaults re-applied');
  }

  destroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    this.uiManager.closeAllMultiSelects();
    this.eventManager.destroy();
    this.container.innerHTML = '';
  }
}